import { useState } from "react";
import { useProfileDraft } from "@/context/ProfileDraftContext";
import { trpc } from "@/lib/trpc";
import { SECTION_METADATA, getSectionItemCount } from "@/components/content/types";
import type { SectionType } from "@profolio/validators";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, ArrowRight, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import type { PageItem } from "./types";
import type { SectionData } from "@profolio/templates";

interface PageSectionsModalProps {
  page: PageItem | null;
  allPages: PageItem[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PageSectionsModal({
  page,
  allPages,
  open,
  onOpenChange,
}: PageSectionsModalProps) {
  const { draft, dispatch, flushAutosave } = useProfileDraft();
  const utils = trpc.useUtils();
  const [movingSectionId, setMovingSectionId] = useState<string | null>(null);

  if (!page) return null;

  const sections = draft?.sections || [];

  // Home page owns all sections that have pageId matching homePage.id OR section.pageId is null/undefined
  const assignedSections = sections.filter((s) => {
    if (page.isHome) {
      return !s.pageId || s.pageId === page.id;
    }
    return s.pageId === page.id;
  });

  const otherPages = allPages.filter((p) => p.id !== page.id);

  const handleMoveSection = async (section: SectionData, targetPage: PageItem) => {
    dispatch({
      type: "REASSIGN_SECTION_PAGE",
      payload: {
        sectionId: section.id,
        pageId: targetPage.id,
      },
    });

    const meta = SECTION_METADATA[section.type as SectionType];
    const sectionName = meta?.label || section.type;
    toast.success(`Moved "${sectionName}" to ${targetPage.title}!`);
    setMovingSectionId(null);

    try {
      if (typeof flushAutosave === "function") {
        await flushAutosave();
      }
      await utils.pages.listPages.invalidate();
    } catch (err) {
      console.error("Failed to persist section move or update pages:", err);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Sections on ${page.title}`}
      description={`${page.isHome ? "Root landing page" : `/${page.slug}`} • ${assignedSections.length} section(s) assigned`}
      className="max-w-lg"
    >
      <div className="space-y-4 pt-2">
        <div className="space-y-2.5 max-h-[55vh] overflow-y-auto pr-1">
          {assignedSections.length === 0 ? (
            <div className="text-center py-8 px-4 rounded-xl border border-dashed border-border bg-slate-50/50 dark:bg-muted/10 space-y-2">
              <p className="text-xs font-semibold text-foreground">No sections on this page</p>
              <p className="text-[11px] text-muted-foreground max-w-xs mx-auto">
                Sections provide content for your page. You can reassign sections here from your Home page or other sub-pages.
              </p>
            </div>
          ) : (
            assignedSections.map((sec) => {
              const meta = SECTION_METADATA[sec.type as SectionType];
              const Icon = meta?.icon || FileText;
              const itemCount = getSectionItemCount(sec);
              const isMoving = movingSectionId === sec.id;

              return (
                <div
                  key={sec.id}
                  className="p-3 rounded-xl border border-border bg-card hover:border-border/80 transition-all space-y-2.5"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="h-7 w-7 rounded-md bg-muted flex items-center justify-center shrink-0">
                        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-foreground truncate">
                            {meta?.label || sec.type}
                          </span>
                          {sec.visible ? (
                            <span className="flex items-center gap-1 text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">
                              <Eye className="h-2.5 w-2.5" /> Visible
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-[10px] text-muted-foreground font-medium">
                              <EyeOff className="h-2.5 w-2.5" /> Hidden
                            </span>
                          )}
                        </div>
                        {itemCount !== null && (
                          <p className="text-[10px] text-muted-foreground">
                            {itemCount} item{itemCount === 1 ? "" : "s"}
                          </p>
                        )}
                      </div>
                    </div>

                    {otherPages.length > 0 && (
                      <Button
                        type="button"
                        variant={isMoving ? "secondary" : "outline"}
                        size="sm"
                        className="h-7 text-xs gap-1"
                        onClick={() => setMovingSectionId(isMoving ? null : sec.id)}
                      >
                        <ArrowRight className="h-3 w-3" />
                        Move
                      </Button>
                    )}
                  </div>

                  {isMoving && (
                    <div className="p-2.5 rounded-lg bg-muted/50 border border-border space-y-2 animate-in fade-in duration-150">
                      <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                        Reassign to page:
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                        {otherPages.map((target) => (
                          <button
                            key={target.id}
                            type="button"
                            onClick={() => handleMoveSection(sec, target)}
                            className="flex items-center justify-between p-2 rounded-md bg-card hover:bg-primary/5 hover:border-primary/40 border border-border text-left transition-colors cursor-pointer text-xs"
                          >
                            <span className="font-medium text-foreground truncate">
                              {target.title}
                            </span>
                            <Badge variant="outline" className="text-[9px] px-1 py-0 ml-1 shrink-0">
                              {target.isHome ? "Home" : `/${target.slug}`}
                            </Badge>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="flex items-center justify-end pt-3 border-t">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
